import * as OperatorDataState from '../../common/state/OperatorDataState.ts';
import { OperatorAppRoutes } from '../config/OperatorRoutingConfig.tsx';
import { OperatorLoginData } from '../../common/state/OperatorLoginData.ts';

function routeModuleActive(
    route: OperatorAppRoutes,
    operatorData: OperatorDataState.OperatorData,
    login: OperatorLoginData,
): boolean {
    const type = getRouteType(route);
    switch (type) {
        case OperatorRouteType.Default:
            return true;
        case OperatorRouteType.ParkingPay:
            return (
                operatorData.hasParkingpayModule &&
                login.allowedPermitTypes.length > 0
            );
        case OperatorRouteType.Parkingabo:
            return (
                operatorData.hasParkingpayModule &&
                login.allowedProductTypes.length > 0
            );
        case OperatorRouteType.Onstreet:
            return (
                operatorData.hasOnstreetTransactions ||
                operatorData.hasTaxomexModule ||
                operatorData.hasEnforcement ||
                operatorData.hasActiveOnstreetZone
            );
        case OperatorRouteType.Offstreet:
            return (
                operatorData.existingOffstreetTransactionsType != null &&
                operatorData.hasPermissionsForOffstreetZones
            );
        case OperatorRouteType.External:
            return (
                operatorData.parkomaticSmartfolio ||
                operatorData.taxomexMiofficeIframe ||
                operatorData.taxomexParkMonitorIframe
            );
    }
}

export function isRouteAllowed(
    operatorData: OperatorDataState.OperatorData,
    login: OperatorLoginData,
    route: OperatorAppRoutes,
): boolean {
    if (operatorData.enforcementOnly) {
        return [
            OperatorAppRoutes.Home,
            OperatorAppRoutes.Logins,
            OperatorAppRoutes.LoginDetail,
            OperatorAppRoutes.LoginCreate,
            OperatorAppRoutes.LoginProfile,
            OperatorAppRoutes.Password,
            OperatorAppRoutes.ConfirmOtp,
            OperatorAppRoutes.Downloads,
            OperatorAppRoutes.EnforcementLog,
            OperatorAppRoutes.EnforcementLogDetail,
        ].includes(route);
    }

    const permissions = login.permissions;

    if (!permissions.operatorAppActive) {
        return false;
    }

    if (!routeModuleActive(route, operatorData, login)) {
        return false;
    }

    switch (route) {
        case OperatorAppRoutes.LoginCreate:
            return login.permissions.userPermissionEdit;
        case OperatorAppRoutes.ClearancesPermitsList:
            return (
                operatorData.licensePlatePermitSettings.active &&
                (permissions.permitsRead || permissions.whitelistCreate)
            );
        case OperatorAppRoutes.ClearanceRequests:
            return (
                operatorData.licensePlatePermitSettings.hasClearanceRequests &&
                permissions.whitelistCreate
            );
        case OperatorAppRoutes.ActivityStatement:
            return (
                operatorData.settings.activityStatement &&
                permissions.activityStatement &&
                permissions.masterDataAndStatistics
            );
        case OperatorAppRoutes.OnstreetTransactions:
        case OperatorAppRoutes.OnstreetTransactionsContractDetail:
            return (
                (operatorData.hasOnstreetTransactions ||
                    operatorData.hasTaxomexModule) &&
                permissions.transactions &&
                operatorData.hasPermissionsForOnstreetZones
            );
        case OperatorAppRoutes.StatisticsCheckins:
            return (
                permissions.transactions && permissions.masterDataAndStatistics
            );
        case OperatorAppRoutes.StatisticsPermits:
            return (
                permissions.permitsRead &&
                operatorData.showPermitStatistics &&
                permissions.masterDataAndStatistics
            );
        case OperatorAppRoutes.CustomerAccounts:
            return (
                (operatorData.hasAWhitelistCustomerPermittype ||
                    operatorData.hasAPermittypeCustomerGrant) &&
                (permissions.whitelistCreate || permissions.whitelistDelete)
            );
        case OperatorAppRoutes.SpecialReports:
            return operatorData.licensePlatePermitSettings
                .hasMobilityStatisticExport;
        case OperatorAppRoutes.EnforcementLog:
        case OperatorAppRoutes.EnforcementLogDetail:
            return operatorData.hasEnforcement && permissions.enforcementLog;
        case OperatorAppRoutes.ParkingMeters:
        case OperatorAppRoutes.UsbSticks:
            return (
                operatorData.hasTaxomexModule &&
                permissions.taxomexModule &&
                operatorData.hasPermissionsForOnstreetZones
            );
        case OperatorAppRoutes.OffstreetTransactions:
        case OperatorAppRoutes.OffstreetTransactionsTicketDetail:
        case OperatorAppRoutes.OffstreetTransactionsTicketLostDetail:
        case OperatorAppRoutes.OffstreetTransactionsEntryWithoutExitDetail:
        case OperatorAppRoutes.OffstreetTransactionsContractDetail:
            return (
                permissions.transactions &&
                operatorData.existingOffstreetTransactionsType !== null
            );
        case OperatorAppRoutes.Parkmonitor:
            return operatorData.taxomexParkMonitorIframe;
        case OperatorAppRoutes.MiOffice:
            return operatorData.taxomexMiofficeIframe;
        case OperatorAppRoutes.ParkingaboUsers:
        case OperatorAppRoutes.ParkingaboUsersAdd:
        case OperatorAppRoutes.ParkingaboUsersInvite:
        case OperatorAppRoutes.ParkingaboUsersDetail:
        case OperatorAppRoutes.ParkingaboUsersProducts:
        case OperatorAppRoutes.ParkingaboUsersProductsAdd:
        case OperatorAppRoutes.ParkingaboUsersProductsDetail:
        case OperatorAppRoutes.ParkingaboUsersTransactions:
        case OperatorAppRoutes.ParkingaboUsersVehicles:
        case OperatorAppRoutes.ParkingaboUsersVehiclesAdd:
        case OperatorAppRoutes.ParkingaboUsersVehiclesDetail:
        case OperatorAppRoutes.ParkingaboUsersAccountStatement:
            return (
                operatorData.numberOfActiveParkingaboRoles > 0 &&
                permissions.parkingaboUserEdit
            );
        case OperatorAppRoutes.ParkingaboRoles:
            return (
                operatorData.numberOfActiveParkingaboRoles > 1 &&
                permissions.parkingaboUserEdit
            );
        case OperatorAppRoutes.Zones:
        case OperatorAppRoutes.ZonesList:
        case OperatorAppRoutes.ZonesDetail:
            return (
                operatorData.hasActiveOnstreetZone &&
                operatorData.hasPermissionsForOnstreetZones
            );
        case OperatorAppRoutes.MobileCloudConnectorSettings:
        case OperatorAppRoutes.CloudConnectorGenerateGuestPermit:
        case OperatorAppRoutes.CloudConnectorGenerateGuestPermitSuccess:
        case OperatorAppRoutes.CloudConnectorGenerateGuestPermitError:
            return operatorData.hasCloudConnector;
        case OperatorAppRoutes.CloudConnectorPeripheries:
        case OperatorAppRoutes.CloudConnectorPeripheriesFullscreen:
        case OperatorAppRoutes.MobileCloudConnectorPeripheries:
        case OperatorAppRoutes.CloudConnectorPeripheriesDetail:
        case OperatorAppRoutes.CloudConnectorPayStationManualCorrection:
        case OperatorAppRoutes.CloudConnectorPeripheryDetailPresence:
        case OperatorAppRoutes.CloudConnectorPeripheryDetailStatus:
            return (
                operatorData.hasCloudConnector &&
                permissions.cloudConnectorOffstreetDevicesView
            );
        case OperatorAppRoutes.CloudConnectorRecodeStatements:
            return (
                operatorData.hasCloudConnector &&
                permissions.cloudConnectorRecodeTicket
            );
        case OperatorAppRoutes.CloudConnectorAutoRecode:
        case OperatorAppRoutes.CloudConnectorAutoRecodeCreate:
        case OperatorAppRoutes.CloudConnectorAutoRecodeEdit:
            return (
                operatorData.hasCloudConnector &&
                permissions.cloudConnectorAutoRecode
            );
        case OperatorAppRoutes.CloudConnectorRecodeQrCodes:
        case OperatorAppRoutes.CloudConnectorRecodeQrCodesAdd:
            return (
                operatorData.hasCloudConnector &&
                operatorData.hasCloudConnectorRecodeQrCodes &&
                permissions.cloudConnectorRecodeTicket
            );
        case OperatorAppRoutes.CloudConnectorRecode:
        case OperatorAppRoutes.CloudConnectorRecodeSuccessResponse:
        case OperatorAppRoutes.CloudConnectorRecodeErrorResponse:
        case OperatorAppRoutes.CloudConnectorRecodeLicsenPlateSerach:
        case OperatorAppRoutes.CloudConnectorRecodeEntryWithoutExit:
        case OperatorAppRoutes.CloudConnectorRecodeEntryWithoutExitCondition:
            return (
                operatorData.hasCloudConnector &&
                permissions.cloudConnectorRecodeTicket
            );
        case OperatorAppRoutes.PayStationStatement:
        case OperatorAppRoutes.PayStationCollections:
            return (
                operatorData.hasCloudConnector &&
                permissions.cloudConnectorReportingAccess
            );
        case OperatorAppRoutes.CloudConnectorCounterOverview:
        case OperatorAppRoutes.CloudConnectorCounterDetail:
        case OperatorAppRoutes.MobileCloudCounterOverview:
        case OperatorAppRoutes.MobileCloudCounterDetail:
            return (
                operatorData.hasCloudConnector &&
                operatorData.hasActiveCounters &&
                permissions.cloudConnectorOffstreetDevicesView
            );
        case OperatorAppRoutes.ParkingaboProducts:
        case OperatorAppRoutes.ParkingaboProductTypes:
            return (
                operatorData.hasActiveParkingaboProducts &&
                permissions.parkingpayModule
            );
        case OperatorAppRoutes.QuotasOverview:
            return (
                operatorData.hasQuotaConfigs &&
                permissions.masterDataAndStatistics
            );

        case OperatorAppRoutes.PermitTypes:
        case OperatorAppRoutes.PermitTypesDetail:
            return permissions.parkingpayModule;
        case OperatorAppRoutes.CloudConnectorSmartCall:
        case OperatorAppRoutes.CloudConnectorSmartCallProfiles:
        case OperatorAppRoutes.CloudConnectorSmartCallProfileAdd:
        case OperatorAppRoutes.CloudConnectorSmartCallProfileDetails:
        case OperatorAppRoutes.CloudConnectorSmartCallWeeklyPlan:
        case OperatorAppRoutes.CloudConnectorSmartCallIndividualDates:
            return operatorData.hasCloudConnector;
        case OperatorAppRoutes.OffstreetGuestProducts:
            return operatorData.hasGuestProducts;
        case OperatorAppRoutes.OffstreetGuestReservationAdd:
        case OperatorAppRoutes.OffstreetGuestReservationAddSuccess:
            return operatorData.hasActiveGuestReservationTemplate;
        case OperatorAppRoutes.OffstreetGuestDayPassOverview:
        case OperatorAppRoutes.OffstreetGuestDayPassAdd:
            return operatorData.hasActiveGuestDayPassTemplateOrStack;
        case OperatorAppRoutes.OffstreetEvents:
        case OperatorAppRoutes.OffstreetEventCards:
            return operatorData.hasOffstreetEvents && permissions.eventsRead;
        case OperatorAppRoutes.OffstreetEventsAdd:
        case OperatorAppRoutes.OffstreetEventEdit:
        case OperatorAppRoutes.OffstreetEventCardsEditStack:
        case OperatorAppRoutes.OffstreetEventCardsAddStack:
            return operatorData.hasOffstreetEvents && permissions.eventsWrite;
        case OperatorAppRoutes.Tariffs:
        case OperatorAppRoutes.TariffDetails:
            return permissions.masterDataAndStatistics;
        case OperatorAppRoutes.Downloads:
        case OperatorAppRoutes.LoginProfile:
        case OperatorAppRoutes.Password:
        case OperatorAppRoutes.MobileHomePage:
        case OperatorAppRoutes.PeripheryHistory:
        case OperatorAppRoutes.Home:
        case OperatorAppRoutes.Parkings:
        case OperatorAppRoutes.ParkingsList:
        case OperatorAppRoutes.ParkingsDetail:
        case OperatorAppRoutes.Logins:
        case OperatorAppRoutes.LoginDetail:
        case OperatorAppRoutes.ConfirmOtp:
        case OperatorAppRoutes.ParkingaboProductsDetail:
        case OperatorAppRoutes.ParkingaboProductTypesDetail:
        case OperatorAppRoutes.QuotasDetail:
        case OperatorAppRoutes.QuotasReduction:
        case OperatorAppRoutes.Privacy:
            return true;
    }
}

export enum OperatorRouteType {
    Default,
    Onstreet,
    Offstreet,
    ParkingPay,
    Parkingabo,
    External,
}

export function getRouteType(route: OperatorAppRoutes): OperatorRouteType {
    switch (route) {
        case OperatorAppRoutes.Home:
        case OperatorAppRoutes.Tariffs:
        case OperatorAppRoutes.TariffDetails:
        case OperatorAppRoutes.Logins:
        case OperatorAppRoutes.LoginCreate:
        case OperatorAppRoutes.LoginDetail:
        case OperatorAppRoutes.ConfirmOtp:
        case OperatorAppRoutes.StatisticsCheckins:
        case OperatorAppRoutes.StatisticsPermits:
        case OperatorAppRoutes.ActivityStatement:
        case OperatorAppRoutes.QuotasDetail:
        case OperatorAppRoutes.QuotasReduction:
        case OperatorAppRoutes.MobileHomePage:
        case OperatorAppRoutes.Privacy:
        case OperatorAppRoutes.QuotasOverview:
        case OperatorAppRoutes.CloudConnectorRecode:
        case OperatorAppRoutes.CloudConnectorRecodeSuccessResponse:
        case OperatorAppRoutes.CloudConnectorRecodeErrorResponse:
        case OperatorAppRoutes.CloudConnectorRecodeLicsenPlateSerach:
        case OperatorAppRoutes.CloudConnectorRecodeEntryWithoutExit:
        case OperatorAppRoutes.CloudConnectorRecodeEntryWithoutExitCondition:
        case OperatorAppRoutes.Downloads:
        case OperatorAppRoutes.Password:
        case OperatorAppRoutes.LoginProfile:
            return OperatorRouteType.Default;

        case OperatorAppRoutes.OnstreetTransactions:
        case OperatorAppRoutes.OnstreetTransactionsContractDetail:
        case OperatorAppRoutes.EnforcementLog:
        case OperatorAppRoutes.EnforcementLogDetail:
        case OperatorAppRoutes.Zones:
        case OperatorAppRoutes.ZonesList:
        case OperatorAppRoutes.ZonesDetail:
        case OperatorAppRoutes.ParkingMeters:
        case OperatorAppRoutes.UsbSticks:
            return OperatorRouteType.Onstreet;

        case OperatorAppRoutes.OffstreetTransactions:
        case OperatorAppRoutes.OffstreetTransactionsTicketDetail:
        case OperatorAppRoutes.OffstreetTransactionsTicketLostDetail:
        case OperatorAppRoutes.OffstreetTransactionsEntryWithoutExitDetail:
        case OperatorAppRoutes.OffstreetTransactionsContractDetail:
        case OperatorAppRoutes.OffstreetGuestProducts:
        case OperatorAppRoutes.OffstreetGuestReservationAdd:
        case OperatorAppRoutes.OffstreetGuestReservationAddSuccess:
        case OperatorAppRoutes.OffstreetGuestDayPassOverview:
        case OperatorAppRoutes.OffstreetGuestDayPassAdd:
        case OperatorAppRoutes.OffstreetEvents:
        case OperatorAppRoutes.OffstreetEventsAdd:
        case OperatorAppRoutes.OffstreetEventEdit:
        case OperatorAppRoutes.OffstreetEventCards:
        case OperatorAppRoutes.OffstreetEventCardsEditStack:
        case OperatorAppRoutes.OffstreetEventCardsAddStack:
        case OperatorAppRoutes.Parkings:
        case OperatorAppRoutes.ParkingsList:
        case OperatorAppRoutes.ParkingsDetail:
        case OperatorAppRoutes.CloudConnectorSmartCall:
        case OperatorAppRoutes.CloudConnectorSmartCallProfiles:
        case OperatorAppRoutes.CloudConnectorSmartCallProfileAdd:
        case OperatorAppRoutes.CloudConnectorSmartCallProfileDetails:
        case OperatorAppRoutes.CloudConnectorSmartCallWeeklyPlan:
        case OperatorAppRoutes.CloudConnectorSmartCallIndividualDates:
        case OperatorAppRoutes.CloudConnectorPeripheries:
        case OperatorAppRoutes.CloudConnectorRecodeStatements:
        case OperatorAppRoutes.CloudConnectorAutoRecode:
        case OperatorAppRoutes.CloudConnectorAutoRecodeCreate:
        case OperatorAppRoutes.CloudConnectorAutoRecodeEdit:
        case OperatorAppRoutes.CloudConnectorRecodeQrCodes:
        case OperatorAppRoutes.CloudConnectorRecodeQrCodesAdd:
        case OperatorAppRoutes.CloudConnectorPeripheriesFullscreen:
        case OperatorAppRoutes.CloudConnectorPeripheriesDetail:
        case OperatorAppRoutes.CloudConnectorPayStationManualCorrection:
        case OperatorAppRoutes.CloudConnectorPeripheryDetailPresence:
        case OperatorAppRoutes.CloudConnectorPeripheryDetailStatus:
        case OperatorAppRoutes.CloudConnectorCounterOverview:
        case OperatorAppRoutes.CloudConnectorCounterDetail:
        case OperatorAppRoutes.CloudConnectorGenerateGuestPermit:
        case OperatorAppRoutes.CloudConnectorGenerateGuestPermitSuccess:
        case OperatorAppRoutes.CloudConnectorGenerateGuestPermitError:
        case OperatorAppRoutes.MobileCloudConnectorPeripheries:
        case OperatorAppRoutes.MobileCloudConnectorSettings:
        case OperatorAppRoutes.MobileCloudCounterOverview:
        case OperatorAppRoutes.MobileCloudCounterDetail:
        case OperatorAppRoutes.PayStationStatement:
        case OperatorAppRoutes.PayStationCollections:
        case OperatorAppRoutes.PeripheryHistory:
            return OperatorRouteType.Offstreet;

        case OperatorAppRoutes.ClearancesPermitsList:
        case OperatorAppRoutes.ClearanceRequests:
        case OperatorAppRoutes.CustomerAccounts:
        case OperatorAppRoutes.PermitTypes:
        case OperatorAppRoutes.PermitTypesDetail:
        case OperatorAppRoutes.SpecialReports:
            return OperatorRouteType.ParkingPay;

        case OperatorAppRoutes.ParkingaboUsers:
        case OperatorAppRoutes.ParkingaboUsersAdd:
        case OperatorAppRoutes.ParkingaboUsersInvite:
        case OperatorAppRoutes.ParkingaboUsersDetail:
        case OperatorAppRoutes.ParkingaboUsersProducts:
        case OperatorAppRoutes.ParkingaboUsersProductsAdd:
        case OperatorAppRoutes.ParkingaboUsersProductsDetail:
        case OperatorAppRoutes.ParkingaboUsersTransactions:
        case OperatorAppRoutes.ParkingaboUsersVehicles:
        case OperatorAppRoutes.ParkingaboUsersVehiclesAdd:
        case OperatorAppRoutes.ParkingaboUsersVehiclesDetail:
        case OperatorAppRoutes.ParkingaboUsersAccountStatement:
        case OperatorAppRoutes.ParkingaboProducts:
        case OperatorAppRoutes.ParkingaboProductsDetail:
        case OperatorAppRoutes.ParkingaboRoles:
        case OperatorAppRoutes.ParkingaboProductTypes:
        case OperatorAppRoutes.ParkingaboProductTypesDetail:
            return OperatorRouteType.Parkingabo;

        case OperatorAppRoutes.Parkmonitor:
        case OperatorAppRoutes.MiOffice:
            return OperatorRouteType.External;
    }
}
